import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";

/**
 * Chip compacto con el comercio (Company en el BFF) de una conversación o
 * contacto. Devuelve null si no hay comercio y no se pasa `emptyLabel`, para
 * poder soltarlo sin condicionales en el llamador.
 *
 * `onClick` opcional lo convierte en botón para abrir el CommerceSelector.
 *
 * GOTCHA HISTÓRICO: este componente pintaba clases `commerce-badge*` que NO
 * existen en ningún scss del repo, más un `material-symbols-outlined` sin
 * dimensionar. Resultado: el ícono salía a su tamaño de icon-font (~24px),
 * desalineado, y el texto heredaba la tipografía ambiente. De ahí que el
 * "nombre del comercio" se viera fuera de escala en toda la lista. Ahora va con
 * tokens y sin ícono: el nombre solo ya identifica al comercio, y el chip queda
 * a la misma altura (18px) que el resto de chips de la fila.
 */
export default function CommerceBadge({
  commerceName,
  commerceId,
  onClick,
  variant = "default",
  size = "md",
  emptyLabel,
  className,
}) {
  const { t } = useTranslation();
  const hasCommerce = !!(commerceId && commerceName);

  if (!hasCommerce && !emptyLabel) return null;

  const label = hasCommerce ? commerceName : emptyLabel;
  const Tag = onClick ? "button" : "span";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      title={
        hasCommerce
          ? `${t("Comercio")}: ${commerceName}`
          : t("Sin comercio asignado")
      }
      className={cn(
        "inline-flex items-center rounded-full font-medium min-w-0 max-w-full",
        // 11px es el piso de legibilidad del design system: `sm` solo aprieta el
        // padding, nunca baja el tamaño de letra (el 10px quedó desterrado como
        // rol de texto porque era ilegible dentro de un chip de 16px).
        size === "sm" ? "px-1.5 h-[18px] text-[11px]" : "px-2 h-[18px] text-[11px]",
        hasCommerce
          ? "bg-status-neutral-soft text-fg-muted"
          : "border border-dashed border-border bg-transparent text-fg-subtle",
        onClick &&
          "hover:bg-bg-subtle hover:text-fg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        variant === "solid" && hasCommerce && "bg-bg-subtle text-fg",
        className,
      )}
    >
      <span className="truncate">{label}</span>
    </Tag>
  );
}
